import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import _noop from 'lodash/noop';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import { mainCq } from '../../../styles/helpers/main-cq';
import {
    FavoriteTeamCardV1 as FavoriteTeamCard,
    BASE_WIDTH_V1 as TEAM_CARD_BASE_WIDTH,
    BASE_HEIGHT_V1 as TEAM_CARD_BASE_HEIGHT,
} from '../../FavoriteTeamCard';
import PlayerCard, {
    BASE_WIDTH as PLAYER_CARD_BASE_WIDTH,
    BASE_HEIGHT as PLAYER_CARD_BASE_HEIGHT,
} from '../../PlayerCard';
import CarouselComponent, {
    Item,
    CAROUSEL_ITEM_PADDING,
} from '../../Carousel';

import { CardType } from './constants';

const LAYOUT_ITEMS = {
    xs: 3,
    sm: 3,
    md: 5,
    lg: 7,
    xl: 8,
};
const PARTIAL_VISIBILITY_GUTTER = {
    xs: 0,
    sm: 0,
    md: 0,
    lg: 0,
    xl: 0,
};
const TEAM_WIDTH = PLAYER_CARD_BASE_HEIGHT * TEAM_CARD_BASE_WIDTH / TEAM_CARD_BASE_HEIGHT;
const cardWidthXl = `${((TEAM_WIDTH + CAROUSEL_ITEM_PADDING.xl) * (100 / LAYOUT_ITEMS.xl)) / (PLAYER_CARD_BASE_WIDTH + CAROUSEL_ITEM_PADDING.xl)}%`;
const cardWidthLg = `${((TEAM_WIDTH + CAROUSEL_ITEM_PADDING.lg) * (100 / LAYOUT_ITEMS.lg)) / (PLAYER_CARD_BASE_WIDTH + CAROUSEL_ITEM_PADDING.lg)}%`;
const cardWidthXs = `${((TEAM_WIDTH + CAROUSEL_ITEM_PADDING.xs) * (100 / LAYOUT_ITEMS.md)) / (PLAYER_CARD_BASE_WIDTH + CAROUSEL_ITEM_PADDING.xs)}%`;
const cardWidths = [
    {
        mediaQuery: `(min-width: ${BREAKPOINTS.xl})`,
        width: cardWidthXl,
    },
    {
        mediaQuery: `(min-width: ${BREAKPOINTS.lg})`,
        width: cardWidthLg,
    },
    {
        width: cardWidthXs,
    },
];

const Carousel = styled(CarouselComponent)`
    margin: -1vw 0;

    ${Item} {
        padding: 1vw ${CAROUSEL_ITEM_PADDING.xs}px;

        &:first-of-type {
            width: ${cardWidthXs};
        }
    }

    ${mainCq.lg`
        ${Item} {
            padding: 1vw ${CAROUSEL_ITEM_PADDING.lg}px;
        }
        ${Item}:first-of-type {
            width: ${cardWidthLg};
        }
    `}
    ${mainCq.xl`
        ${Item}:first-of-type {
            width: ${cardWidthXl};
        }
    `}
`;

const LargerView = (props) => {

    const {
        players,
        teamId,
        teamName,
        teamLink,
        teamLogoImageTemplateUrl,
        backgroundImageTemplateUrl,
        overlayStyles,
        playerImageTemplateUrl,
        loading,
        onItemClick,
        onAfterChange,
    } = props;

    const renderItem = useCallback((item, isLoading, index = 0) => {

        return (index === 0) ? (
            <FavoriteTeamCard
                as={Link}
                {...item}
                loading={isLoading}
                onClick={() => onItemClick({
                    id: teamName,
                    type: CardType.TEAM,
                })}
            />
        ) : (
            <PlayerCard
                as={Link}
                {...item}
                loading={isLoading}
                onClick={() =>
                    onItemClick({
                        id: item.id ? item.name : CardType.EDIT,
                        index: index - 1,
                        type: item.id ? CardType.PLAYER : CardType.EDIT,
                    })}
            />
        );
    }, [onItemClick]);

    const list = useMemo(() => ([
        {
            id: teamId,
            name: teamName,
            to: teamLink,
            teamLogoImageTemplateUrl,
            backgroundImageTemplateUrl,
            overlayStyles,
            playerImageTemplateUrl,
            cardWidths,
        },
        ...players,
    ]), [teamId, teamName, teamLink, teamLogoImageTemplateUrl, backgroundImageTemplateUrl, overlayStyles, playerImageTemplateUrl]);

    return (
        <Carousel
            isLoading={loading}
            list={list}
            renderItem={renderItem}
            partialVisibilityGutter={PARTIAL_VISIBILITY_GUTTER}
            layoutItems={LAYOUT_ITEMS}
            onAfterChange={onAfterChange}
        />
    );

};

LargerView.propTypes = {
    teamId: PropTypes.number,
    teamName: PropTypes.string,
    teamLink: PropTypes.string,
    teamLogoImageTemplateUrl: PropTypes.string,
    backgroundImageTemplateUrl: PropTypes.string,
    overlayStyles: PropTypes.string,
    playerImageTemplateUrl: PropTypes.string,
    players: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            lastName: PropTypes.string,
            name: PropTypes.string,
            to: PropTypes.string,
        }).isRequired,
    ),
    loading: PropTypes.bool,
    onItemClick: PropTypes.func,
    onAfterChange: PropTypes.func,
};

LargerView.defaultProps = {
    loading: false,
    onItemClick: _noop,
    onAfterChange: _noop,
};

export default memo(LargerView);
